import { IConfiguration } from '../../../../domain/conf'
import { Utils } from '../../../../utils/common'
import { DOMEvents } from '../../../../utils/dom'
import { Store } from '../../../../utils/storage/keys'
import { LocalStore } from '../../../../utils/storage/local'
import { AnalyticsService } from '../../../analytics'
import { IdentityEvent } from '../../../common/events/identity'
import { PageType } from '../../../common/page-types'
import { IProfile, Profile } from '../../../common/profile'
import { IIdentityServiceAdapter } from '../adapter'
import { ArticleBlock } from '../article-block'

interface IWindow extends Window {
    Connext: any
    MG2Loader: any
    pageTag: string
}

interface INavigaPlugin {
    name: string
    initOptions: any
}

export enum PaywallLevel {
    NOT_METERED = 'not_metered',
    METERED = 'metered',
    REGISTRATION_LOCKED = 'registration_locked',
    PREMIUM_LOCKED = 'premium_locked',
}

export enum MeterLevel {
    FREE = 1,
    METERED = 2,
    PREMIUM = 3,
}

declare let window: IWindow

export class NavigaAuth0Adapter implements IIdentityServiceAdapter {
    private static pageType: string
    private fingerprintPlugin: INavigaPlugin
    private g2InsightsPlugin: INavigaPlugin
    private engagePlugin: INavigaPlugin

    constructor(private pageData: IConfiguration) {
        const auth0Config = pageData.identity?.auth0
        const navigaConfig = pageData.identity?.naviga

        NavigaAuth0Adapter.pageType = pageData.page?.type || ''

        // Naviga plugin configurations
        // tslint:disable-next-line:max-line-length
        // Reference: https://docs.navigaglobal.com/engage/-MGi9-KKLwnWfoD36Ot6/getting-started-with-engage/configuring-engage-on-your-website/setting-up-the-inititalization-script#initialization-script-options
        if (auth0Config && navigaConfig) {
            const attr = navigaConfig.engage?.attr
            const settingsKey = navigaConfig.engage?.settingsKey

            this.fingerprintPlugin = {
                initOptions: {
                    environment: navigaConfig.environment || '',
                    version: navigaConfig.fingerprint?.version || '',
                },
                name: 'FP',
            }

            this.g2InsightsPlugin = {
                initOptions: {
                    collectors: navigaConfig.g2Insights?.collectors || [],
                    containerId: navigaConfig.g2Insights?.containerId || '',
                    tagManager: navigaConfig.g2Insights?.tagManager || '',
                    version: navigaConfig.g2Insights?.version || '',
                },
                name: 'DL',
            }

            this.engagePlugin = {
                initOptions: {
                    authSettings: {
                        // Auth0 configurations
                        client_id: auth0Config.clientId || '',
                        domain: auth0Config.domain || '',
                        redirect_uri: `${location.origin}/auth0-redirect/`,
                    },
                    clientCode: navigaConfig.engage?.clientCode || '',
                    configCode: navigaConfig.engage?.configCode || '',
                    debug: navigaConfig.debug || false,
                    environment: navigaConfig.environment || '',
                    publicEventHandlers: {
                        // List of Naviga Engage events:
                        // https://docs.navigaglobal.com/engage/-MGi9-KKLwnWfoD36Ot6/fundamentals/engage-events
                        onAnyAccountStatusEvent: NavigaAuth0Adapter.handleOnAnyAccountStatusEvent,
                        onConversationDetermined: NavigaAuth0Adapter.handleOnConversationDetermined,
                        onInit: NavigaAuth0Adapter.handleOnInitEvent,
                        onLoginSuccess: NavigaAuth0Adapter.handleOnLoginSuccessEvent,
                        onPaywallShown: NavigaAuth0Adapter.handleOnPaywallShownEvent
                    },
                    siteCode: navigaConfig.engage?.siteCode || '',
                },
                name: 'NXT',
            }

            // Add attr if it exists in page-data
            if (attr) {
                this.engagePlugin.initOptions.attr = attr
            }

            // Add settingsKey if it exists in page-data
            if (settingsKey) {
                this.engagePlugin.initOptions.settingsKey = settingsKey
            }
        }
    }

    /**
     * On any account status event
     *
     * Fires when Naviga Engage checks the Account status
     */
    public static handleOnAnyAccountStatusEvent(eventData: any) {
        const profile = NavigaAuth0Adapter.getProfileFromEventData(eventData)
        const isLoginSuccessful = sessionStorage.getItem(Store.NAVIGA_AUTH0_LOGIN_SUCCESS) || false

        if (!profile || NavigaAuth0Adapter.pageType === PageType.AUTH0_REDIRECT) {
            return
        }

        if (isLoginSuccessful && profile.email) {
            // Back to where login started
            // Remove login success session storage
            sessionStorage.removeItem(Store.NAVIGA_AUTH0_LOGIN_SUCCESS)

            // Update profile
            document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_CHANGE, {
                ...profile,
                isLogin: true
            }))

            // Wait for profile to be updated
            const onIdentityProfileUpdated = () => {
                // Remove identity profile updated event listener
                document.removeEventListener(IdentityEvent.IDENTITY_PROFILE_UPDATED, onIdentityProfileUpdated)

                // Sign in successful
                document.dispatchEvent(new CustomEvent(IdentityEvent.IDENTITY_LOGIN, {
                    detail: {
                        email: profile.email,
                        uuid: profile.uuid
                    }
                }))

                // Send sign in analytics event
                document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                    detail: {
                        control_fields: {
                            mparticle: {
                                keys: {
                                    integration_name: 'integration_name',
                                },
                                mp_event_type: 'UserContent'
                            }
                        },
                        event_name: AnalyticsService.customEventNames.signin,
                        integration_name: 'Naviga_Auth0',
                    }
                }))
            }

            document.addEventListener(IdentityEvent.IDENTITY_PROFILE_UPDATED, onIdentityProfileUpdated)

            return
        }

        // Update profile
        document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_CHANGE, profile))
    }

    /**
     * On conversation determined event
     */
    public static handleOnConversationDetermined(eventData: any) {
        // Get the meter level: Free = 1, Metered = 2 and Premium = 3
        // https://docs.navigaglobal.com/engage/-MGi9-KKLwnWfoD36Ot6/fundamentals/engage-commands/configuration-commands
        const meterLevel = window.Connext.Storage.GetCurrentMeterLevel()

        if (meterLevel === MeterLevel.METERED) {
            const meterName = eventData.EventData?.Name || 'undefined'

            // Send meter execute event
            document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        mparticle: {
                            keys: {
                                meter_name: 'meter_name',
                            },
                            mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                            mp_event_type: 'UserPreference'
                        }
                    },
                    event_name: AnalyticsService.customEventNames.meterExecute,
                    meter_name: meterName // Naviga Engage conversation name
                }
            }))
        }
    }

    /**
     * On init event
     */
    public static handleOnInitEvent() {
        const signInLoader = document.querySelector('.sso-btns .sign-in-spinner')

        // Remove sign in loader
        if (signInLoader) {
            signInLoader.remove()
        }
    }

    /**
     * On login success event
     */
    public static handleOnLoginSuccessEvent() {
        // Set login success session storage flag
        // Workaround to prevent having missed login events on the redirect page
        sessionStorage.setItem(Store.NAVIGA_AUTH0_LOGIN_SUCCESS, 'true')
    }

    /**
     * On paywall shown event
     */
    public static handleOnPaywallShownEvent(eventData: any) {
        // Get the meter level: Free = 1, Metered = 2 and Premium = 3
        const meterLevel = window.Connext.Storage.GetCurrentMeterLevel()
        const meterName = eventData.Conversation?.Name || 'undefined'
        const modalName = eventData.EventData?.Name || 'undefined'

        // Remove widgets and ads below the featured image
        ArticleBlock.removeWidgetsAndAds()

        // Send metered modal view event
        if (meterLevel === MeterLevel.METERED) {
            document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        mparticle: {
                            keys: {
                                meter_name: 'meter_name',
                                modal_name: 'modal_name'
                            },
                            mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                            mp_event_type: 'UserPreference'
                        }
                    },
                    event_name: AnalyticsService.customEventNames.modalView,
                    meter_name: meterName, // Naviga Engage conversation name
                    modal_name: modalName  // Naviga Engage paywall action name
                }
            }))
        }

        // Send premium modal view event
        if (meterLevel === MeterLevel.PREMIUM) {
            document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        mparticle: {
                            keys: {
                                modal_name: 'modal_name'
                            },
                            mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                            mp_event_type: 'UserPreference'
                        }
                    },
                    event_name: AnalyticsService.customEventNames.modalView,
                    modal_name: modalName // Naviga Engage paywall action name
                }
            }))
        }
    }

    /**
     * Get profile from Naviga event data
     */
    private static getProfileFromEventData(eventData: any): IProfile {
        const authProfile = eventData.AuthProfile
        const subscriptionData = eventData.MG2AccountData
        let profile: IProfile

        // Get Auth0 profile details
        if (authProfile) {
            profile = { ...profile,
                email: authProfile.email,
                emailConfirmed: authProfile.email_verified,
                name: authProfile.name,
                uuid: authProfile.sub
            }

            // Get Naviga profile details
            if (subscriptionData) {
                const isPremium = subscriptionData.DigitalAccess?.AccessLevel?.IsPremium

                if (isPremium) {
                    // Premium digital access
                    profile.adLight = true
                } else {
                    // No premium digital access
                    profile.adLight = false
                }
            }
        }

        return profile
    }

    /**
     * Initalize Naviga plugins
     */
    public init() {
        const logoutButton = document.querySelector('[data-mg2-action=logout]')
        if (window.MG2Loader) {
            // Load Naviga plugins
            window.MG2Loader.init(
                {
                    plugins: [
                        this.fingerprintPlugin,
                        this.g2InsightsPlugin,
                        this.engagePlugin
                    ]
                }
            )
        }

        // Add paywall level page tag
        this.setPaywallLevel()

        // Logout button click event
        if (logoutButton) {
            logoutButton.addEventListener('click', () => {
                this.signOut()
            })
        }
    }

    /**
     * Sign out
     */
    public signOut() {
        const localProfile = LocalStore.get<IProfile>(Store.ID)
        const anonymous: IProfile = new Profile()

        // Clear profile
        if (localProfile) {
            anonymous.id = localProfile.id
        }

        anonymous.uuid = undefined
        anonymous.adLight = false
        anonymous.emailConfirmed = undefined
        anonymous.email = undefined
        anonymous.name = undefined

        // Update profile to anonymous
        document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_CHANGE, {...anonymous, isLogin: false}))

        // Send sign out analytics event
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            integration_name: 'integration_name'
                        },
                        mp_event_type: 'UserContent'
                    }
                },
                event_name: AnalyticsService.customEventNames.signout,
                integration_name: 'Naviga_Auth0',
            }
        }))

        // Sign out successful
        document.dispatchEvent(new CustomEvent(IdentityEvent.IDENTITY_SIGNOUT))
    }

    /**
     * Check if there is an auth0_sub cookie
     */
    public hasSession(): boolean {
        const auth0Cookie = (document.cookie.split(';').find((c) =>
                c.trim().startsWith('auth0_sub')) || '').trim().split('=').pop() || undefined
        return !!auth0Cookie
    }

    /**
     * Define paywall level base on page data
     */
    public setPaywallLevel() {
        const page = this.pageData.page
        let pageTag: string

        if (page) {
            if (page.isPremium) {
                // premium locked
                pageTag = PaywallLevel.PREMIUM_LOCKED
            } else if (page.isRegistration) {
                // registration only
                pageTag = PaywallLevel.REGISTRATION_LOCKED
            } else if (page.isPaywallWhitelisted) {
                // non-metered content
                pageTag = PaywallLevel.NOT_METERED
            } else {
                // metered content
                pageTag = PaywallLevel.METERED
            }

            window.pageTag = pageTag
        }
    }
}
